import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import { EllipsisVertical, Package, Wrench } from "lucide-react";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ViewPackageDetails from "./components/ViewPackageDetails";
import ManagePackage from "./components/ManagePackage.js";
import getStatusColor from "./utils/StatusColor.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu.js";
import { fetchPackagesAction } from "../../../state/actions/packageActions";
import { formatDate } from "utils/dateFormatter";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import LoadingSpinner from "components/Loading/LoadingSpinner.js";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

const PAGE_SIZE = 20;

const PackageManagement = () => {
  const dispatch = useDispatch();
  const { packages, loading, error } = useSelector((state) => state.packages || {});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isViewPackageDrawerOpen, setViewPackageDrawerOpen] = useState(false);
  const [isManagePackageDrawerOpen, setManagePackageDrawerOpen] = useState(false);

  const isLoadingInitial = loading && page === 1;
  const isLoadingMore = loading && page > 1;
  const observerRef = useRef();

  const formatMoney = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `P${num.toFixed(2)}` : "N/A";
  };


  const fetchMorePackages = useCallback(async () => {
    const res = await dispatch(fetchPackagesAction(null, null, page, PAGE_SIZE));
    if (res?.success) {
      if ((res.data || []).length < PAGE_SIZE) {
        setHasMore(false);
      } else {
        setPage((prev) => prev + 1);
      }
    } else {
      setHasMore(false);
    }
  }, [dispatch, page]);


  const lastRowRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchMorePackages();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, fetchMorePackages]
  );

  useEffect(() => {
    dispatch(fetchPackagesAction(null, null, 1, PAGE_SIZE));
  }, [dispatch]);

  const handleViewPackage = (pkg) => {
    setSelectedPackage(pkg);
    setViewPackageDrawerOpen(true);
  };

  const handleManagePackage = (pkg) => {
    setSelectedPackage(pkg);
    setManagePackageDrawerOpen(true);
  };


  const tableData = useMemo(() => {
    if (!Array.isArray(packages)) return [];

    return packages.map((pkg) => {
      const packageObj = pkg?.package || pkg?.package_data || pkg; 

      const images = Array.isArray(packageObj?.package_images)
        ? packageObj.package_images.map((img) => `/${img}`)
        : [];

      const senderName = pkg?.sender
        ? `${pkg.sender.first_name} ${pkg.sender.last_name}`
        : packageObj?.sender_id;

      const recipientName = pkg?.recipient
        ? `${pkg.recipient.first_name} ${pkg.recipient.last_name}`
        : packageObj?.recipient_id;

      const claimRequest = packageObj?.claim_request ?? pkg?.claim_request ?? false;

      return {
        packageId: packageObj?.package_id,
        description: packageObj?.package_description,
        images,
        sender: senderName,
        recipient: recipientName,
        packageNumber: packageObj?.package_number,
        status: packageObj?.package_status,
        pickup: packageObj?.pickup_location,
        dropoff: packageObj?.dropoff_location,
        type: packageObj?.delivery_type,
        createdAt: packageObj?.created_at ? formatDate(packageObj.created_at) : "N/A",

        price: formatMoney(packageObj?.price),

        claim_request: claimRequest,

        dimensions: `${packageObj?.package_length ?? "-"}×${packageObj?.package_width ?? "-"}×${packageObj?.package_height ?? "-"} cm`,
        weight: `${packageObj?.package_weight ?? "-"} kg`,

        original: pkg,
      };
    });
  }, [packages]);

  // Columns definition
  const columnsData = [
    {
      Header: "Item",
      accessor: "description",
      width: "10%",
    },
    {
      Header: "Price",
      accessor: "price",
      width: "12%",
    },

    {
  Header: "Claim Request",
  accessor: "claim_request",
  Cell: ({ value }) => (
    <StatusBadge
      label={value ? "Requested" : "Not Requested"}
      variant={value ? "success" : "warning"}
    />
  ),
  width: "10%",
},

    {
      Header: "Route",
      accessor: "route",
      Cell: ({ row }) => (
        <Box>
          <Text>
            {row.original.pickup} → {row.original.dropoff}
          </Text>
        </Box>
      ),
      width: "20%",
    },
    {
  Header: "Status",
  accessor: "status",
  Cell: ({ value }) => (
    <StatusBadge
      label={value}
      variant={getStatusVariant(value)}
    />
  ),
  width: "10%",
},
    {
      Header: "Sender",
      accessor: "sender",
      Cell: ({ row }) => <Text>{row.original.sender}</Text>,
      width: "15%",
    },
    {
      Header: "Recipient",
      accessor: "recipient",
      Cell: ({ row }) => <Text>{row.original.recipient}</Text>,
      width: "15%",
    },
    {
      Header: "Created",
      accessor: "createdAt",
      width: "10%",
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Package size={16} />,
              text: "View Package",
              onClick: () => handleViewPackage(row.original),
            },
            {
              icon: <Wrench size={16} />,
              text: "Manage Package",
              onClick: () => handleManagePackage(row.original),
            },
          ]}
        />
      ),
      width: "5%",
    },
  ];

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "25px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          {isLoadingInitial ? (
            <Skeleton height="20px" width="120px" />
          ) : (
            <Text fontSize="md" fontWeight="bold">
              All Packages: {packages?.length ?? 0}
            </Text>
          )}
        </Flex>
      </Box>

      <ReusableTable
        columnsData={columnsData}
        tableData={tableData}
        isLoading={isLoadingInitial}
        error={error}
        lastRowRef={lastRowRef}
      />

      {isLoadingMore && <LoadingSpinner mt={4} py={2} />}

      <ReusableDrawer
        isOpen={isViewPackageDrawerOpen}
        onClose={() => setViewPackageDrawerOpen(false)}
        headerContent={
          selectedPackage && (
            <Text fontSize="lg" fontWeight="bold">
              View Package - {selectedPackage.packageNumber} {selectedPackage.description}
            </Text>
          )
        }
        bodyContent={
          selectedPackage && (
            <ViewPackageDetails
              package={selectedPackage.original?.package}
              sender={selectedPackage.original?.sender}
              recipient={selectedPackage.original?.recipient}
              onClose={() => setViewPackageDrawerOpen(false)}
            />
          )
        }
        size="lg"
      />

      <ReusableDrawer
        isOpen={isManagePackageDrawerOpen}
        onClose={() => setManagePackageDrawerOpen(false)}
        headerContent={
          selectedPackage && (
            <Text fontSize="lg" fontWeight="bold">
              Manage Package - #{selectedPackage.packageNumber}
            </Text>
          )
        }
        bodyContent={
          selectedPackage && (
            <ManagePackage
              package={selectedPackage}
              onClose={() => setManagePackageDrawerOpen(false)}
            />
          )
        }
        size="lg"
      />
    </Flex>
  );
};

export default PackageManagement;